import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { Provider } from "@redotech/redo-model/order";
import { SPECIAL_RESTOCK_ID } from "@redotech/redo-model/return-flow";
import {
  type SetReturnLocationsState,
  SPECIAL_LOCATION_ID,
} from "@redotech/redo-model/return-flow-step-valid";
import { StepTypeDetailsProps } from "@redotech/redo-return-ui/flows/step";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext } from "react";
import { LocationOptionsContext } from "../flows/location-options-context";
import { LocationCard } from "./location-card";

export const SetReturnLocationsDetails = memo(
  function SetReturnLocationsDetails({
    state,
    setState,
  }: StepTypeDetailsProps<SetReturnLocationsState>) {
    const locationOptions = useContext(LocationOptionsContext);
    const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
    const team = useContext(TeamContext);

    const resolvedLocations = useLoad(
      async (signal) => {
        const addressIds = state.locations.filter(
          (id) =>
            id !== SPECIAL_LOCATION_ID.ORIGINAL &&
            id !== SPECIAL_LOCATION_ID.SHIPMENT_ORIGIN &&
            id !== SPECIAL_LOCATION_ID.REDO_MANAGED &&
            id !== "",
        );

        if (addressIds.length === 0) {
          return { addresses: [], externalMap: new Map<string, string>() };
        }

        const result = await rpcClient.getAddressesByIds(
          { ids: addressIds },
          { signal },
        );

        const externalMap = new Map<string, string>();
        for (const addr of result.addresses) {
          if (addr.type !== "custom" && addr.externalId) {
            externalMap.set(addr._id.toString(), addr.externalId);
          }
        }

        return { addresses: result.addresses, externalMap };
      },
      [state.locations, rpcClient],
    );

    const handleAddLocation = () => {
      setState((prev) => ({ ...prev, locations: [...prev.locations, ""] }));
    };

    const handleDeleteLocation = (addressId: string) => {
      setState((prev) => ({
        ...prev,
        locations: prev.locations.filter((id) => id !== addressId),
      }));
    };

    const handleLocationSelect = (
      oldAddressId: string,
      newAddressId: string,
      newExternalLocationId: string | undefined,
      index: number,
    ) => {
      setState((prev) => {
        const newLocations = prev.locations.map((id, i) =>
          i === index ? newAddressId : id,
        );

        const newRestockIds = { ...prev.restockExternalLocationIds };

        let defaultRestockId: string | null = null;

        // For non-Shopify merchants, always use NO_RESTOCK since we don't support it yet.
        if (team?.platform !== Provider.SHOPIFY) {
          defaultRestockId = SPECIAL_RESTOCK_ID.NO_RESTOCK;
        } else
          switch (newAddressId) {
            case SPECIAL_LOCATION_ID.ORIGINAL: {
              // Original location → default to "Back to fulfillment location"
              defaultRestockId = SPECIAL_RESTOCK_ID.BACK_TO_FULFILLMENT;

              break;
            }
            case SPECIAL_LOCATION_ID.SHIPMENT_ORIGIN: {
              // Shipment origin address → default to "No restock"
              defaultRestockId = SPECIAL_RESTOCK_ID.NO_RESTOCK;

              break;
            }
            case SPECIAL_LOCATION_ID.REDO_MANAGED: {
              // Redo managed → default to "No restock"
              defaultRestockId = SPECIAL_RESTOCK_ID.NO_RESTOCK;

              break;
            }
            default:
              if (!newExternalLocationId) {
                // Custom location (no external ID) → default to "Back to fulfillment location"
                defaultRestockId = SPECIAL_RESTOCK_ID.BACK_TO_FULFILLMENT;
              } else {
                defaultRestockId = newExternalLocationId;
              }
          }

        newRestockIds[newAddressId] = defaultRestockId;

        if (oldAddressId !== newAddressId && oldAddressId in newRestockIds) {
          delete newRestockIds[oldAddressId];
        }

        return {
          ...prev,
          locations: newLocations,
          restockExternalLocationIds: newRestockIds,
        };
      });
    };

    const handleRestockLocationSelect = (
      addressId: string,
      restockLocationId: string | null,
    ) => {
      setState((prev) => ({
        ...prev,
        restockExternalLocationIds: {
          ...prev.restockExternalLocationIds,
          [addressId]: restockLocationId,
        },
      }));
    };
    if (!locationOptions) {
      return null;
    }
    return (
      <Flex dir="column" gap="md">
        <Text fontSize="xs" textColor="tertiary">
          The actual shipping location will be determined by the cheapest
          available shipping rate.
        </Text>
        <Flex dir="column" gap="3xl">
          {state.locations.map((locationId, index) => (
            <LocationCard
              addressId={locationId}
              externalLocationId={resolvedLocations.value?.externalMap.get(
                locationId,
              )}
              index={index}
              key={`${locationId}-${index}`}
              locationOptions={locationOptions}
              onDelete={handleDeleteLocation}
              onLocationSelect={(newAddressId, newExternalLocationId) =>
                handleLocationSelect(
                  locationId,
                  newAddressId,
                  newExternalLocationId,
                  index,
                )
              }
              onRestockLocationSelect={handleRestockLocationSelect}
              resolvedAddresses={resolvedLocations.value?.addresses ?? []}
              restockExternalLocationId={
                state.restockExternalLocationIds?.[locationId]
              }
              selectedLocations={state.locations}
            />
          ))}
        </Flex>
        <Flex justify="flex-start" mt="2xl">
          <RedoButton
            hierarchy="secondary"
            IconLeading={() => <>+</>}
            onClick={handleAddLocation}
            size="xs"
            text="Add location"
          />
        </Flex>
      </Flex>
    );
  },
);
